import React, { useState, useRef } from 'react';
import { Image as ImageIcon, Camera, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export const ImageUpload = ({ value, onChange }) => {
  const [imagePreview, setImagePreview] = useState(value || '');
  const [showWebcam, setShowWebcam] = useState(false);
  const [stream, setStream] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setImagePreview(base64String);
      onChange(base64String);
    };
    reader.readAsDataURL(file);
  };

  const startWebcam = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      setStream(mediaStream);
      setShowWebcam(true);
      
      // Wait for video element to be ready
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      toast.error('Failed to access webcam');
      console.error('Webcam error:', error);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);

    const base64Image = canvas.toDataURL('image/jpeg', 0.8);
    setImagePreview(base64Image);
    onChange(base64Image);
    stopWebcam();
  };

  const stopWebcam = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowWebcam(false);
  };

  const clearImage = () => {
    setImagePreview('');
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      {/* Buttons */}
      {!imagePreview && !showWebcam && (
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            className="flex-1 h-12 rounded-lg border border-cardBorder hover:bg-surface font-semibold text-grayDark"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Image
          </Button>
          <Button
            type="button"
            onClick={startWebcam}
            variant="outline"
            className="flex-1 h-12 rounded-lg border border-cardBorder hover:bg-surface font-semibold text-grayDark"
          >
            <Camera className="w-4 h-4 mr-2" />
            Take Photo
          </Button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Webcam View */}
      {showWebcam && (
        <div className="relative rounded-lg overflow-hidden border-2 border-cardBorder bg-black">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full h-64 object-cover"
          />
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
            <Button
              type="button"
              onClick={capturePhoto}
              className="bg-primary hover:bg-primary/90 text-primaryDark font-bold rounded-full w-16 h-16"
            >
              <Camera className="w-6 h-6" />
            </Button>
            <Button
              type="button"
              onClick={stopWebcam}
              variant="outline"
              className="bg-white hover:bg-surface text-grayDark font-bold rounded-full w-16 h-16"
            >
              <X className="w-6 h-6" />
            </Button>
          </div>
        </div>
      )}

      {/* Image Preview */}
      {imagePreview && (
        <div className="relative rounded-lg overflow-hidden border-2 border-cardBorder">
          <img 
            src={imagePreview} 
            alt="Preview" 
            className="w-full h-64 object-cover"
          />
          <button
            type="button"
            onClick={clearImage}
            className="absolute top-2 right-2 p-2 bg-voteDown-bg text-voteDown-text rounded-full hover:bg-voteDown-text hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <p className="text-xs text-grayMeta text-center">
        {!imagePreview && !showWebcam && 'Upload from gallery or take a photo (max 5MB)'}
      </p>
    </div>
  );
};
